import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const DEFAULT_WARNING_DELAY = 25 * 60 * 1000;
const DEFAULT_GRACE_PERIOD = 5 * 60 * 1000;

const useSessionTimeout = ({
    onLogout,
    warningDelay = DEFAULT_WARNING_DELAY,
    gracePeriod = DEFAULT_GRACE_PERIOD,
}) => {
    const [isSessionAlertVisible, setIsSessionAlertVisible] = useState(false);
    const [remainingGraceSeconds, setRemainingGraceSeconds] = useState(
        gracePeriod / 1000
    );

    const warningTimerRef = useRef(null);
    const logoutTimerRef = useRef(null);
    const countdownIntervalRef = useRef(null);
    const isSessionAlertVisibleRef = useRef(false);

    const totalDuration = warningDelay + gracePeriod;

    const clearWarningTimer = useCallback(() => {
        if (warningTimerRef.current) {
            clearTimeout(warningTimerRef.current);
            warningTimerRef.current = null;
        }
    }, []);

    const clearGracePeriod = useCallback(() => {
        if (logoutTimerRef.current) {
            clearTimeout(logoutTimerRef.current);
            logoutTimerRef.current = null;
        }

        if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
        }
    }, []);

    const clearSessionAlertState = useCallback(() => {
        isSessionAlertVisibleRef.current = false;
        setIsSessionAlertVisible(false);
        setRemainingGraceSeconds(gracePeriod / 1000);
        clearWarningTimer();
        clearGracePeriod();
    }, [clearGracePeriod, clearWarningTimer, gracePeriod]);

    const performLogout = useCallback(() => {
        clearSessionAlertState();
        localStorage.clear();
        onLogout();
    }, [clearSessionAlertState, onLogout]);

    const openSessionAlert = useCallback(() => {
        if (isSessionAlertVisibleRef.current) {
            return;
        }

        isSessionAlertVisibleRef.current = true;
        setIsSessionAlertVisible(true);
        setRemainingGraceSeconds(gracePeriod / 1000);
        clearGracePeriod();

        const deadline = Date.now() + gracePeriod;

        logoutTimerRef.current = setTimeout(() => {
            performLogout();
        }, gracePeriod);

        countdownIntervalRef.current = setInterval(() => {
            const secondsLeft = Math.max(
                0,
                Math.ceil((deadline - Date.now()) / 1000)
            );
            setRemainingGraceSeconds(secondsLeft);
        }, 1000);
    }, [clearGracePeriod, gracePeriod, performLogout]);

    const resumeSessionTimeout = useCallback(() => {
        clearWarningTimer();

        if (isSessionAlertVisibleRef.current) {
            return;
        }

        warningTimerRef.current = setTimeout(() => {
            openSessionAlert();
        }, warningDelay);
    }, [clearWarningTimer, openSessionAlert, warningDelay]);

    const pauseSessionTimeout = useCallback(() => {
        clearSessionAlertState();
    }, [clearSessionAlertState]);

    const handleContinueSession = useCallback(() => {
        clearSessionAlertState();
        resumeSessionTimeout();
    }, [clearSessionAlertState, resumeSessionTimeout]);

    const handleImmediateLogout = useCallback(() => {
        performLogout();
    }, [performLogout]);

    useEffect(() => {
        const handleActivity = () => {
            if (isSessionAlertVisibleRef.current) {
                return;
            }

            resumeSessionTimeout();
        };

        window.addEventListener("mousemove", handleActivity);
        window.addEventListener("keydown", handleActivity);
        window.addEventListener("click", handleActivity);
        window.addEventListener("scroll", handleActivity, true);

        resumeSessionTimeout();

        return () => {
            window.removeEventListener("mousemove", handleActivity);
            window.removeEventListener("keydown", handleActivity);
            window.removeEventListener("click", handleActivity);
            window.removeEventListener("scroll", handleActivity, true);
            clearWarningTimer();
            clearGracePeriod();
        };
    }, [clearGracePeriod, clearWarningTimer, resumeSessionTimeout]);

    const sessionMetrics = useMemo(
        () => ({
            gracePeriodProgress: (remainingGraceSeconds / (gracePeriod / 1000)) * 100,
            warningDelayMinutes: warningDelay / 60000,
            gracePeriodMinutes: gracePeriod / 60000,
            totalDurationMinutes: totalDuration / 60000,
        }),
        [gracePeriod, remainingGraceSeconds, totalDuration, warningDelay]
    );

    return {
        isSessionAlertVisible,
        remainingGraceSeconds,
        ...sessionMetrics,
        pauseSessionTimeout,
        resumeSessionTimeout,
        handleContinueSession,
        handleImmediateLogout,
    };
};

export default useSessionTimeout;